// src/components/Header.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { bokunDataSrc, BOKUN_EXPERIENCE } from '../constants/booking';

// Styled Components
const HeaderContainer = styled.header`
    background-color: #ffffff;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
    position: sticky;
    top: 0;
    z-index: 1000;
    border-bottom: 1px solid #f1f5f9;
`;

const Nav = styled.nav`
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
    height: 80px;

    @media (max-width: 768px) {
        padding: 0 1rem;
        height: 70px;
    }
`;

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
`;

const Logo = styled.div`
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @media (max-width: 768px) {
        width: 56px;
        height: 56px;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: transform 0.3s ease;
    }

    &:hover img {
        transform: scale(1.05);
    }
`;

// Logo + booking-partner badges, kept together on the left of the nav.
const LeftCluster = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;

    @media (max-width: 768px) {
        gap: 0.6rem;
    }
`;

const PlatformBadges = styled.div`
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding-left: 1rem;
    border-left: 1px solid #e5e7eb;

    @media (max-width: 768px) {
        gap: 0.5rem;
        padding-left: 0.55rem;
    }
`;

const BadgeLink = styled.a`
    display: inline-flex;
    align-items: center;
    opacity: 0.92;
    transition: transform 0.2s ease, opacity 0.2s ease;

    img {
        height: var(--badge-h, 24px);
        width: auto;
        display: block;
    }

    &:hover {
        opacity: 1;
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        img {
            height: calc(var(--badge-h, 24px) * 0.6);
        }
    }
`;

const NavItems = styled.ul`
    display: flex;
    list-style: none;
    gap: 3rem;
    margin: 0;
    padding: 0;

    @media (max-width: 768px) {
        display: none;
    }
`;

const NavItem = styled.li`
    display: flex;
    align-items: center;
`;

interface NavLinkProps {
    $isActive: boolean;
}

const NavLink = styled.a<NavLinkProps>`
    text-decoration: none;
    color: ${props => props.$isActive ? '#667eea' : '#6b7280'};
    font-weight: ${props => props.$isActive ? '700' : '500'};
    font-size: 1.1rem;
    padding: 0.5rem 0;
    position: relative;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        color: #667eea;
        transform: translateY(-1px);
    }

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: ${props => props.$isActive ? '100%' : '0'};
        height: 3px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 2px;
        transition: width 0.3s ease;
    }

    &:hover::after {
        width: 100%;
    }
`;

const AuthSection = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;

    @media (max-width: 768px) {
        display: none;
    }
`;

const AuthButton = styled.button`
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    font-family: inherit;
    border: none;
    padding: 0.85rem 2.25rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 1.05rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
    }
`;

const MobileMenuButton = styled.button`
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    padding: 0.5rem;
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
        background: #f8fafc;
        color: #667eea;
    }

    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;
  background-color: #ffffff;
  border-top: 1px solid #f1f5f9;
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const MobileNavLink = styled.a<NavLinkProps>`
  text-decoration: none;
  color: ${props => props.$isActive ? '#667eea' : '#6b7280'};
  font-weight: ${props => props.$isActive ? '700' : '500'};
  font-size: 1.1rem;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  cursor: pointer;
  background-color: ${props => props.$isActive ? '#f8fafc' : 'transparent'};
  border: 2px solid ${props => props.$isActive ? '#667eea' : 'transparent'};

  &:hover {
    color: #667eea;
    background-color: #f8fafc;
    transform: translateX(5px);
  }
`;

const MobileAuthButton = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
  }
`;

// Main Header Component
interface HeaderProps {
    activePage: string;
    onPageChange: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ activePage, onPageChange }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navigationItems = [
        { id: 'home', label: 'Home' },
        { id: 'gallery', label: 'Gallery' },
        { id: 'trips', label: 'Trips' },
        { id: 'contact', label: 'Contact' }
    ];

    // Per-logo heights: these logos have very different aspect ratios (GetYourGuide
    // is nearly square, Viator/Tripadvisor are wide wordmarks), so a single height
    // makes GetYourGuide look small. Tuned individually for even visual weight.
    const bookingPartners = [
        {
            name: 'GetYourGuide',
            src: '/logo-getyourguide.svg',
            height: '38px',
            href: 'https://www.getyourguide.com/drl-business-s596781/'
        },
        {
            name: 'Viator',
            src: '/logo-viator.png',
            height: '22px',
            href: 'https://www.viator.com/tours/Vienna/Cultural-Guided-Tour-of-Slovak-Castles-and-Bratislava/d454-5575192P15'
        },
        {
            name: 'Tripadvisor',
            src: '/logo-tripadvisor.svg',
            height: '22px',
            href: 'https://www.tripadvisor.com/Attraction_Review-g274924-d33216216-Reviews-DRL_business_s_r_o-Bratislava_Bratislava_Region.html'
        }
    ];

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleLogoClick = () => {
        onPageChange('home');
        setIsMobileMenuOpen(false);
    };

    const handleNavClick = (pageId: string) => {
        onPageChange(pageId);
        setIsMobileMenuOpen(false);
    };

    return (
        <HeaderContainer>
            <Nav>
                <LeftCluster>
                    <LogoContainer onClick={handleLogoClick}>
                        <Logo>
                            <img
                                src="/logo.png"
                                alt="DRL Business Logo"
                                onError={(e) => {
                                    // Fallback if logo doesn't exist
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        const fallback = document.createElement('div');
                                        fallback.style.width = '45px';
                                        fallback.style.height = '45px';
                                        fallback.style.background = 'linear-gradient(135deg, #667eea, #764ba2)';
                                        fallback.style.borderRadius = '8px';
                                        fallback.style.display = 'flex';
                                        fallback.style.alignItems = 'center';
                                        fallback.style.justifyContent = 'center';
                                        fallback.style.color = 'white';
                                        fallback.style.fontWeight = 'bold';
                                        fallback.style.fontSize = '1rem';
                                        fallback.innerHTML = 'DRL';
                                        parent.appendChild(fallback);
                                    }
                                }}
                            />
                        </Logo>
                    </LogoContainer>

                    <PlatformBadges aria-label="Book our tours on">
                        {bookingPartners.map((partner) => (
                            <BadgeLink
                                key={partner.name}
                                href={partner.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Book our tours on ${partner.name}`}
                                aria-label={`Book our tours on ${partner.name}`}
                                style={{ ['--badge-h' as string]: partner.height } as React.CSSProperties}
                            >
                                <img src={partner.src} alt={`${partner.name} logo`} />
                            </BadgeLink>
                        ))}
                    </PlatformBadges>
                </LeftCluster>

                <NavItems>
                    {navigationItems.map((item) => (
                        <NavItem key={item.id}>
                            <NavLink
                                href={`#${item.id}`}
                                $isActive={activePage === item.id}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNavClick(item.id);
                                }}
                            >
                                {item.label}
                            </NavLink>
                        </NavItem>
                    ))}
                </NavItems>

                <AuthSection>
                    <AuthButton
                        className="bokunButton"
                        data-src={bokunDataSrc(BOKUN_EXPERIENCE.slovakia)}
                    >
                        Book Now
                    </AuthButton>
                </AuthSection>

                <MobileMenuButton onClick={toggleMobileMenu}>
                    {isMobileMenuOpen ? '✕' : '☰'}
                </MobileMenuButton>
            </Nav>

            <MobileMenu $isOpen={isMobileMenuOpen}>
                {navigationItems.map((item) => (
                    <MobileNavLink
                        key={item.id}
                        href={`#${item.id}`}
                        $isActive={activePage === item.id}
                        onClick={(e) => {
                            e.preventDefault();
                            handleNavClick(item.id);
                        }}
                    >
                        {item.label}
                    </MobileNavLink>
                ))}
                <MobileAuthButton
                    className="bokunButton"
                    data-src={bokunDataSrc(BOKUN_EXPERIENCE.slovakia)}
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    Book Now
                </MobileAuthButton>
            </MobileMenu>
        </HeaderContainer>
    );
};

export default Header;